// HEAT LOAD, THE DAIKIN WAY
//
// A line-for-line copy of Daikin's heat load estimate sheet ("Sample Heat
// Load sheet", Summer / Monsoon / Winter, ISHRAE method): the same factors,
// the same safety margins, the same order of sums. Given the same room it
// gives the same Btu/hr and TR as the sheet. Tested against the sheet itself
// (recalculated in a spreadsheet engine) for several rooms.
//
// What it adds to the sheet is only what a website needs: outdoor design
// conditions for many cities (the sheet has Delhi typed in), with the ETD
// correction worked out per city the same way the sheet's ETD tab does.
//
// No imports: scripts/prerender.js loads this file by stripping `export`.

// ─── constants from the sheet ─────────────────────────────────────────────

export const ROOM_DEFAULT = { dbF: 75, rh: 55, gr: 71.52 }; // 75°F, 55% RH (sheet row 11)

// Solar heat gain factor, Btu/hr/sq ft, 30°N, 4 pm ('Solar Heat Gain Factor cal').
export const SHGF = {
  summer: { N: 14, NE: 12, E: 12, SE: 14, S: 12, SW: 100, W: 164, NW: 123, H: 123 },
  monsoon: { N: 11, NE: 11, E: 11, SE: 11, S: 13, SW: 127, W: 165, NW: 100, H: 107 },
};

// Equivalent temperature difference at 4 pm for a 100 lb/sq ft wall, before
// the correction for outdoor-minus-room temperature and daily range ('ETD Calculation').
export const ETD_BASE = { NE: 10, E: 18, SE: 18, S: 16, SW: 14, W: 12, NW: 6, N: 4, ROOF: 32 };

// U values, Btu/hr sq ft °F ('Wall U values').
export const U = {
  wall: 1 / 2.1,         // 0.4762: 12 mm plaster + 230 mm brick + 18 mm plaster
  partition: 1 / 2.41,   // 0.4149: the sheet uses the floor/ceiling value for partitions
  floor: 1 / 2.41,       // 0.4149: 150 mm RCC + plaster + tiles
  ceiling: 1 / 2.41,
  roofBare: 1 / 2.33,    // 0.4292: 150 mm RCC, no insulation
  roofInsulated: 1 / 7.59, // 0.1318: with 50 mm under-deck insulation
  glass: 1.13,
};

export const GLASS = {
  single: { shgc: 0.56, u: 1.13 }, // the sheet's values
  double: { shgc: 0.47, u: 0.55 },
};

export const FACTORS = {
  peopleSensible: 245,   // Btu/hr per person
  peopleLatent: 205,
  lightsFactor: 1.25,    // × watts × 3.42
  btuPerWatt: 3.42,
  equipFactor: 1.25,     // × kW × 3415
  btuPerKW: 3415,
  safetySensible: 10,    // %
  safetyLatent: 5,       // %
  ductGain: 2.5,         // % of room sensible heat, supply duct gain + leakage
  fanGain: 7,            // % of room sensible heat
  freshAirPerPerson: 5,  // cfm
  freshAirPerSqft: 0.06, // cfm
  bypass: 0.15,
};

// Outdoor conditions as the sheet has them for Delhi (ISHRAE): summer
// 110 °F DB / 75 gr, monsoon 95 °F / 152.53 gr, daily range 25 °F.
export const DELHI_DESIGN = {
  summer: { dbF: 110, gr: 75 },
  monsoon: { dbF: 95, gr: 152.53 },
  dailyRangeF: 25,
};

// ETD correction the sheet applies: (outdoor − room) − 15 − (daily range − 20) / 2.
// Delhi summer: 35 − 15 − 2.5 = 17.5 °F; monsoon: 20 − 15 − 2.5 = 2.5 °F.
export const etdCorrection = (tdF, dailyRangeF) => tdF - 15 - (dailyRangeF - 20) / 2;

const DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

// ─── one season ───────────────────────────────────────────────────────────
//
// room = {
//   area, height,                       sq ft, ft
//   glass: { N: sqft, W: sqft, ... },   window area by direction (H = skylight)
//   glassType: 'single' | 'double',
//   walls: { N: sqft, W: sqft, ... },   outside wall area by direction, net of glass
//   roof: 0 | sqft, roofInsulated,      roof exposed to sun (top floor)
//   floor: sqft, ceiling: sqft,         over / under spaces that are not air-conditioned
//   partition: sqft,                    walls to spaces that are not air-conditioned
//   people, lightsW, equipKW,
//   freshAirCfm (optional; else 5 per person + 0.06 per sq ft),
//   safetySensible (optional, %),
// }
// outdoor = { dbF, gr }, inside = { dbF, gr }, dailyRangeF

export function seasonLoad(room, outdoor, inside, dailyRangeF, season) {
  const F = FACTORS;
  const td = outdoor.dbF - inside.dbF;
  const dGr = outdoor.gr - inside.gr;
  const corr = etdCorrection(td, dailyRangeF);
  const tdAdj = td - 5; // floor, ceiling and partitions
  const g = GLASS[room.glassType] || GLASS.single;
  const shgf = SHGF[season] || SHGF.summer;

  let glassSolar = 0;
  let glassArea = 0;
  Object.entries(room.glass || {}).forEach(([dir, a]) => {
    glassSolar += a * (shgf[dir] || 0) * g.shgc;
    glassArea += a;
  });
  let walls = 0;
  Object.entries(room.walls || {}).forEach(([dir, a]) => {
    if (DIRS.includes(dir)) walls += a * (ETD_BASE[dir] + corr) * U.wall;
  });
  const roof = (room.roof || 0) * (ETD_BASE.ROOF + corr) * (room.roofInsulated ? U.roofInsulated : U.roofBare);
  const floor = (room.floor || 0) * tdAdj * U.floor;
  const ceiling = (room.ceiling || 0) * tdAdj * U.ceiling;
  const glassCond = glassArea * td * g.u;
  const partition = (room.partition || 0) * tdAdj * U.partition;
  const people = (room.people || 0) * F.peopleSensible;
  const equip = (room.equipKW || 0) * F.equipFactor * F.btuPerKW;
  const lights = (room.lightsW || 0) * F.lightsFactor * F.btuPerWatt;

  const subtotal = glassSolar + walls + roof + floor + ceiling + glassCond + partition + people + equip + lights;
  const sf = room.safetySensible != null ? room.safetySensible : F.safetySensible;
  const rsh = subtotal * (1 + sf / 100);
  const duct = rsh * F.ductGain / 100;
  const fan = rsh * F.fanGain / 100;
  const cfm = room.freshAirCfm != null
    ? room.freshAirCfm
    : (room.people || 0) * F.freshAirPerPerson + room.area * F.freshAirPerSqft;
  const bf = F.bypass;
  const faSenRoom = cfm * bf * td * 1.08;
  const ersh = rsh + duct + fan + faSenRoom;

  const peopleLat = (room.people || 0) * F.peopleLatent;
  const rlh = peopleLat * (1 + F.safetyLatent / 100);
  const faLatRoom = cfm * bf * dGr * 0.68;
  const erlh = rlh + faLatRoom;

  const oaSen = cfm * (1 - bf) * td * 1.08;
  const oaLat = cfm * (1 - bf) * dGr * 0.68;
  const total = ersh + erlh + oaSen + oaLat;

  return {
    season, td, dGr, corr, cfm,
    lines: { glassSolar, walls, roof, floor, ceiling, glassCond, partition, people, equip, lights },
    subtotal, rsh, duct, fan, faSenRoom, ersh, peopleLat, rlh, faLatRoom, erlh, oaSen, oaLat,
    total, tr: total / 12000,
  };
}

// Summer and monsoon; the sheet selects the larger ("Sel. TR").
export function heatLoad(room, climate, inside = ROOM_DEFAULT) {
  const summer = seasonLoad(room, climate.summer, inside, climate.dailyRangeF, 'summer');
  const monsoon = seasonLoad(room, climate.monsoon, inside, climate.dailyRangeF, 'monsoon');
  const governing = monsoon.total > summer.total ? monsoon : summer;
  return { summer, monsoon, governing, tr: governing.tr, btu: governing.total };
}

// ─── psychrometrics, for setting the room temperature ─────────────────────

const satPressure = (tC) => 0.61094 * Math.exp((17.625 * tC) / (tC + 243.04)); // kPa

// Humidity ratio in grains/lb at a dry bulb (°F) and RH (%), sea level.
export function grainsAt(dbF, rh, pressureKPa = 101.325) {
  const pw = (rh / 100) * satPressure((dbF - 32) / 1.8);
  return 7000 * 0.62198 * pw / (pressureKPa - pw);
}

// Humidity ratio in grains/lb from dry bulb and wet bulb (°C), sea level, like
// the sheet (ASHRAE Fundamentals, psychrometrics eq. 33). Delhi's 43.3 / 23.9
// gives 74.3 gr against the sheet's 75.
export function grainsFromWB(dbC, wbC, pressureKPa = 101.325) {
  const pws = satPressure(wbC);
  const wsStar = 0.621945 * pws / (pressureKPa - pws);
  const w = ((2501 - 2.326 * wbC) * wsStar - 1.006 * (dbC - wbC)) / (2501 + 1.86 * dbC - 4.186 * wbC);
  return w * 7000;
}

export const cToF = (c) => c * 1.8 + 32;
export const fToC = (f) => (f - 32) / 1.8;

// ═════════════════════════════════════════════════════════════════════════
//  ON TOP OF THE SHEET: what the website adds
// ═════════════════════════════════════════════════════════════════════════
//
// 1. A room from plain answers (size, floor, sun), for people who have not
//    measured every wall.
// 2. Outdoor conditions for any city in data/indiaClimate.js.
// 3. The check the sheet leaves to the engineer: does the AC still have the
//    power on the hottest afternoon, when its own capacity drops?

// Rooms around this one: the average case. Half the inside walls, and half
// the floor and ceiling, face rooms that are not air-conditioned.
export const NEIGHBOUR_SHARE = 0.5;

// An AC gives its full capacity up to a set outdoor temperature (43 °C or
// 46 °C, by series: data/highlights.js) and loses 5% for every °C above it.
export const DERATE_PER_C = 5;

// Where IMD publishes no "hottest day of an average year" for a station:
// its 0.4% design temperature plus this. It is the median gap at the seven
// stations that publish both (Delhi Palam 3.5, Amritsar 4.0, Bengaluru 2.5,
// Bhubaneswar 4.7, Kolkata 2.6, Imphal 3.1, Thiruvananthapuram 2.0).
export const PEAK_ABOVE_DESIGN_C = 3;

export const KW_PER_TR = 3.517;

// Room types: the same defaults the calculator has always used.
export const ROOM_TYPES = {
  bedroom: { people: 2, lightW: 0.5, eqKW: 0.3, glassPct: 0.12, glassType: 'single' },
  living: { people: 4, lightW: 0.8, eqKW: 0.8, glassPct: 0.15, glassType: 'single' },
  study: { people: 2, lightW: 1.0, eqKW: 1.5, glassPct: 0.12, glassType: 'single' },
  kitchen: { people: 2, lightW: 1.2, eqKW: 5.0, glassPct: 0.08, glassType: 'single' },
  office: { people: 6, lightW: 1.2, eqKW: 3.0, glassPct: 0.20, glassType: 'double' },
  shop: { people: 8, lightW: 1.5, eqKW: 1.5, glassPct: 0.25, glassType: 'single' },
};

// The room the sheet needs, from the calculator's answers.
//   a = { type, l, w, height, floor: 'ground'|'mid'|'top', sun: 'N'|'E'|'S'|'W',
//         people, glassArea?, glassType?, roofInsulated?, lightW?, eqKW?, safety? }
// The window is on the longer wall, which is the one outside wall and faces
// the sun the customer picked. The other three walls are inside walls.
export function roomFor(a) {
  const t = ROOM_TYPES[a.type] || ROOM_TYPES.bedroom;
  const h = a.height || 10;
  const long = Math.max(a.l, a.w);
  const short = Math.min(a.l, a.w);
  const area = a.l * a.w;
  const outside = long * h;
  const glass = a.glassArea != null ? Math.min(a.glassArea, outside) : outside * t.glassPct;
  const share = NEIGHBOUR_SHARE;
  const room = {
    area,
    height: h,
    glass: { [a.sun]: glass },
    glassType: a.glassType || t.glassType,
    walls: { [a.sun]: outside - glass },
    partition: (long + 2 * short) * h * share,
    roof: 0,
    roofInsulated: Boolean(a.roofInsulated),
    floor: 0,
    ceiling: 0,
    people: a.people != null ? a.people : t.people,
    lightsW: (a.lightW != null ? a.lightW : t.lightW) * area,
    equipKW: a.eqKW != null ? a.eqKW : t.eqKW,
  };
  if (a.safety != null) room.safetySensible = a.safety;
  if (a.floor === 'top') { room.roof = area; room.floor = area * share; }
  if (a.floor === 'mid') { room.floor = area * share; room.ceiling = area * share; }
  if (a.floor === 'ground') { room.ceiling = area * share; }
  return room;
}

// Outdoor conditions for one station of data/indiaClimate.js.
//   Summer: the hottest afternoon of an average year, with the moisture of
//   the 0.4% design day. Monsoon: the 0.4% wet bulb and its dry bulb.
export function climateFor(st) {
  const peakC = st.peak != null ? st.peak : st.db + PEAK_ABOVE_DESIGN_C;
  const dailyRangeC = st.dr != null ? st.dr : st.hi - st.lo;
  return {
    summer: { dbF: cToF(peakC), gr: grainsFromWB(st.db, st.mcwb) },
    monsoon: { dbF: cToF(st.mcdb), gr: grainsFromWB(st.mcdb, st.wb) },
    dailyRangeF: dailyRangeC * 1.8,
    peakC,
    peakMeasured: st.peak != null,
    monsoonC: st.mcdb,
    dailyRangeC,
  };
}

// Room conditions for a set temperature, at 55% RH like the sheet.
export function insideAt(tC) {
  const dbF = Math.round(cToF(tC));
  if (dbF === ROOM_DEFAULT.dbF) return ROOM_DEFAULT;
  return { dbF, rh: 55, gr: grainsAt(dbF, 55) };
}

// What an AC really gives at an outdoor temperature, in TR.
export function capacityTR(kw, fullC, atC) {
  const over = Math.max(0, atC - fullC);
  return (kw / KW_PER_TR) * Math.max(0, 1 - (DERATE_PER_C / 100) * over);
}

// Everything the sizing needs for one room in one city.
export function needFor(room, climate, inside = ROOM_DEFAULT) {
  const hl = heatLoad(room, climate, inside);
  return {
    hl,
    summerTR: hl.summer.tr,
    monsoonTR: hl.monsoon.tr,
    loadTR: Math.max(hl.summer.tr, hl.monsoon.tr),
    peakC: climate.peakC,
    monsoonC: climate.monsoonC,
  };
}

// Does one AC (kw, full) hold the room on the hottest afternoon AND in the monsoon?
export function fits(unit, need) {
  return capacityTR(unit.kw, unit.full, need.peakC) >= need.summerTR - 1e-9
    && capacityTR(unit.kw, unit.full, need.monsoonC) >= need.monsoonTR - 1e-9;
}
